import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { Copy, ExternalLink, Check, RefreshCw, Share2 } from "lucide-react";
import { Assessments } from "@/lib/api";
import ShareLinkControls from "@/components/ShareLinkControls";
import ConsentNotice from "@/components/ConsentNotice";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

/**
 * Diálogo de partilha com:
 *  - retry exponencial (3 tentativas) na obtenção do token
 *  - link visível e selecionável
 *  - botão copiar com fallback execCommand
 *  - botão abrir em novo separador
 *  - QR code para captura no telemóvel do paciente
 */
export default function ShareDialog({ assessmentId, patientId, open, onOpenChange }) {
  const [gate, setGate] = useState(null);
  const blocked = gate?.mode === "block" && !gate.ok;
  const [link, setLink] = useState("");
  const [qrSrc, setQrSrc] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fatal, setFatal] = useState(false);
  const [copied, setCopied] = useState(false);

  const [info, setInfo] = useState(null);
  const applyInfo = async (inf) => {
    setInfo(inf);
    if (inf.state !== "active") { setLink(""); setQrSrc(""); return; }
    const url = `${window.location.origin}/portal/${inf.token}`;
    setLink(url);
    try {
      setQrSrc(await QRCode.toDataURL(url, { width: 240, margin: 1, color: { dark: "#0f172a", light: "#ffffff" } }));
    } catch { /* QR opcional */ }
  };

  const fetchToken = async () => {
    setLoading(true);
    setError("");
    setFatal(false);
    setLink("");
    setQrSrc("");
    const tries = [0, 800, 2000];
    let lastErr = null;
    for (let i = 0; i < tries.length; i++) {
      if (tries[i]) await new Promise(r => setTimeout(r, tries[i]));
      try {
        await applyInfo(await Assessments.shareToken(assessmentId));
        setLoading(false);
        return;
      } catch (e) {
        lastErr = e;
        const st = e?.response?.status;
        if (st === 403 || st === 404) {
          setError(e.response?.data?.detail || "Não é possível gerar o link para esta avaliação.");
          setFatal(true);
          setLoading(false);
          return;
        }
      }
    }
    console.error("share-token failed", lastErr);
    setError("Não foi possível obter o link. Tenta novamente em alguns segundos.");
    setLoading(false);
  };

  useEffect(() => {
    if (open && assessmentId && gate && !blocked) fetchToken();
    if (!open) { setCopied(false); setGate(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, assessmentId, gate]);

  const copy = async () => {
    if (!link) return;
    let ok = false;
    try { await navigator.clipboard.writeText(link); ok = true; }
    catch { /* fallback */ }
    if (!ok) {
      // Fallback execCommand para iframes/HTTP onde clipboard.writeText falha
      const ta = document.createElement("textarea");
      ta.value = link; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      try { ok = document.execCommand("copy"); } catch { ok = false; }
      document.body.removeChild(ta);
    }
    if (ok) {
      setCopied(true);
      toast.success("Link copiado");
      setTimeout(() => setCopied(false), 2400);
    } else {
      toast.error("Não foi possível copiar — seleciona o link manualmente.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="share-dialog"
        className="max-w-md bg-slate-950 border-slate-800 text-slate-100"
      >
        <DialogHeader>
          <DialogTitle className="text-slate-50 font-display tracking-tight flex items-center gap-2">
            <Share2 className="w-4 h-4 text-emerald-400" /> Partilhar com o paciente
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Link único e seguro. Não partilhes em canais públicos.
          </DialogDescription>
        </DialogHeader>
        <ConsentNotice patientId={patientId} op="partilha" onState={setGate} />

        {loading && (
          <div className="py-10 text-center text-slate-400 text-sm">A gerar link seguro…</div>
        )}

        {error && !loading && (
          <div className="space-y-3 py-2">
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-200 text-sm p-4">
              {error}
            </div>
            {!fatal && <Button onClick={fetchToken} variant="outline" className="w-full rounded-full border-slate-700 text-slate-200">
              <RefreshCw className="w-4 h-4 mr-2" /> Tentar novamente
            </Button>}
          </div>
        )}

        {info && !loading && !error && (
          <ShareLinkControls assessmentId={assessmentId} info={info} onChange={applyInfo} />
        )}

        {link && !loading && (
          <div className="space-y-4">
            {qrSrc && (
              <div className="flex justify-center">
                <div className="rounded-2xl bg-white p-3" data-testid="share-qr">
                  <img src={qrSrc} alt="QR code do portal" className="w-44 h-44" />
                </div>
              </div>
            )}

            <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5">Link</div>
              <div
                data-testid="share-link-value"
                className="text-xs text-emerald-300 break-all font-mono select-all"
              >
                {link}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Button
                data-testid="share-copy-btn"
                onClick={copy}
                className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
              >
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? "Copiado" : "Copiar"}
              </Button>
              <Button
                data-testid="share-open-btn"
                asChild
                variant="outline"
                className="rounded-full border-slate-700 text-slate-200 hover:bg-slate-800"
              >
                <a href={link} target="_blank" rel="noreferrer">
                  <ExternalLink className="w-4 h-4 mr-2" /> Abrir
                </a>
              </Button>
            </div>

            <div className="text-[11px] text-slate-500 text-center">
              O paciente pode aceder ao relatório em qualquer dispositivo lendo o QR ou abrindo o link.
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
